import * as React from "react";
import Calendar, { Detail, OnChangeDateCallback } from "react-calendar";

import { InputContainer, InputContainerProps } from "./input-container";

interface CalendarInputProps extends InputContainerProps {
  onChange: (name: string, value: Date) => void;

  minDetail?: Detail;

  minDate?: Date;
  maxDate?: Date;

  placeholder?: string;

  transformDateToCalendarDateValue: (value: any) => Date;
  presentDate: (date: string) => string;
}

/**
 * So for example when you click on a month in the year view,
 * the click handler on the actual react-calendar will
 * fire before the global click listener you set up to close the calendar on click outside...
 * which will then cause the event.target to be an element that is no longer in the dom,
 * and thus not contained by your reference ->
 * so we traverse the paths of the target and make sure that the click never occurs in something
 * which is a child of "react-calendar" rather than by solely checking the click's target element's bounds.
 */
const isSpecialEdgeCaseWhereCalendarClickHandlerRunsBeforeGlobalClickListener =
  (event: MouseEvent) => {
    let isSpecialCase = false;
    try {
      const path = event.composedPath();
      isSpecialCase = path.some((node) => {
        if (node instanceof HTMLElement) {
          const classList = node.classList;
          if (classList?.contains("react-calendar")) {
            return true;
          }
        }
      });
    } catch (error) {
      isSpecialCase = false;
    }
    return isSpecialCase;
  };

export const CalendarInput: React.FunctionComponent<CalendarInputProps> = (
  props
) => {
  const [showCalendar, setShowCalendar] = React.useState(false);

  const calendarInputRef = React.useRef<HTMLInputElement | null>(null);
  const calendarRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    const handleClickOutsideCalendar = (event: MouseEvent) => {
      if (
        showCalendar &&
        calendarRef.current &&
        calendarInputRef.current &&
        !calendarRef.current.contains(event.target as Node) &&
        !calendarInputRef.current.contains(event.target as Node) &&
        !isSpecialEdgeCaseWhereCalendarClickHandlerRunsBeforeGlobalClickListener(
          event
        )
      ) {
        setShowCalendar(false);
      }
    };

    document.addEventListener("click", handleClickOutsideCalendar);
    return () => {
      document.removeEventListener("click", handleClickOutsideCalendar);
    };
  }, [showCalendar, calendarRef.current, calendarInputRef.current]);

  const toggleCalendar = () => {
    setShowCalendar(!showCalendar);
  };

  const onChange: OnChangeDateCallback = (value, event) => {
    props.onChange(props.name, value);
    setShowCalendar(false);
  };

  return (
    <InputContainer
      {...props}
      className={`calendar-input ${showCalendar ? "focused" : ""}`}
    >
      <div className="input-immediate-container">
        <input
          name={props.name}
          value={props.presentDate(props.form[props.name]) || ""}
          readOnly
          type="text"
          className="input-text"
          title={props.displayName}
          onClick={toggleCalendar}
          ref={calendarInputRef}
          placeholder={props.placeholder}
        />
      </div>

      {showCalendar && (
        <Calendar
          onChange={onChange}
          value={props.transformDateToCalendarDateValue(props.form[props.name])}
          minDetail={props.minDetail}
          minDate={props.minDate}
          maxDate={props.maxDate}
          inputRef={calendarRef}
        />
      )}
    </InputContainer>
  );
};
